const BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000/api";

const TOKEN_KEY = "cg_token";

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = tokenStorage.get();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 204) return undefined as unknown as T;

  const json = await res.json();

  if (!res.ok) {
    const message =
      json?.message ?? json?.error ?? `HTTP ${res.status}`;

    if (res.status === 402 && !path.startsWith("/billing") && window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE("/billing");
    }

    throw new ApiError(message, res.status, json?.errors);
  }

  return json as T;
}

export class ApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(message: string, status: number, errors?: Record<string, string[]>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  postForm: async <T>(path: string, formData: FormData): Promise<T> => {
    const token = tokenStorage.get();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(`${BASE_URL}${path}`, { method: "POST", headers, body: formData });
    const json = await res.json();
    if (!res.ok) {
      const message = json?.message ?? json?.error ?? `HTTP ${res.status}`;
      throw new ApiError(message, res.status, json?.errors);
    }
    return json as T;
  },
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type UserRole = "admin" | "officer" | "scm_manager" | "cfo_hod";

export interface Institution {
  id: number;
  name: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: UserRole;
  institution_id: number;
  institution?: Institution;
}

export interface AuthResponse {
  message: string;
  user: User;
  token: string;
}

export interface PaginatedResponse<T> {
  data: {
    data: T[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface SingleResponse<T> {
  data: T;
}

export interface Contract {
  id: number;
  contract_number: string;
  vendor_name: string;
  supplier_id: number | null;
  supplier?: Supplier | null;
  department: string;
  responsible_officer: string | null;
  description: string | null;
  start_date: string;
  end_date: string;
  contract_value: string | null;
  status: "active" | "expired" | "pending_renewal" | "terminated";
  renewal_status: "not_required" | "required" | "in_progress" | "renewed";
  renewal_terms: string | null;
  last_renewal_date: string | null;
  renewal_days_before: number;
  contract_type: string | null;
  original_term_months: number | null;
  extension_allowed: boolean;
  extension_conditions: string | null;
  has_signed_document: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface HealthScore {
  score: number;
  status: "Excellent" | "Healthy" | "Monitor" | "At Risk" | "Critical";
  reasons: string[];
}

export interface Department {
  id: number;
  name: string;
  description: string | null;
  head_name: string | null;
  head_email: string | null;
  phone: string | null;
  is_active: boolean;
}

export type BBBEEStatus = "Level 1" | "Level 2" | "Level 3" | "Level 4" | "Level 5" | "Level 6" | "Level 7" | "Level 8" | "Non-Compliant" | "Exempt (EME)";

export interface SupplierDepartment {
  id: number;
  name: string;
  pivot: {
    vendor_number: string | null;
  };
}

export interface Supplier {
  id: number;
  name: string;
  vendor_number: string | null;
  bbbee_status: BBBEEStatus | null;
  tax_pin: string | null;
  tax_clearance_validity: string | null;
  csd_number: string | null;
  description: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  country: string | null;
  average_rating: string;
  total_reviews: number;
  status: "active" | "inactive" | "suspended";
}

export interface SupplierReview {
  id: number;
  supplier_id: number;
  reviewed_by_id: number;
  rating: string;
  category: string | null;
  review_text: string | null;
  review_date: string;
  status: "pending" | "approved" | "rejected";
  scores: Record<string, number> | null;
  recommendation: ReviewRecommendation | null;
  quarter: number | null;
  year: number | null;
  supplier?: Supplier;
  reviewed_by?: User;
}

export interface SLAMatrix {
  id: number;
  sla_name: string;
  supplier_id: number;
  service_type: string;
  uptime_percentage: string;
  response_time_hours: number;
  resolution_time_hours: number;
  penalty_clause: string | null;
  effective_from: string;
  effective_to: string | null;
  is_active: boolean;
  supplier?: Supplier;
}

export interface ContractStats {
  total: number;
  active: number;
  expired: number;
  pending_renewal: number;
  terminated: number;
  expiring_30_days: number;
  expiring_90_days: number;
  total_value: number;
  active_value: number;
}

export interface AuditLog {
  id: number;
  contract_id: number;
  user_id: number | null;
  action_type: string;
  action: string;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  user?: User;
}

export interface ContractRenewal {
  id: number;
  contract_id: number;
  decided_by_id: number | null;
  decision: "auto_renew" | "conditional" | "re_advertise" | "extend" | "terminate";
  justification: string;
  new_end_date: string | null;
  decided_at: string;
  created_at: string;
  decided_by?: User;
}

export interface Plan {
  id: number;
  name: string;
  slug: string;
  price: string;
  max_contracts: number | null;
  max_users: number | null;
  has_workflow_engine: boolean;
  is_active: boolean;
}

export type SubscriptionStatus = "trialing" | "active" | "past_due" | "cancelled";

export interface Subscription {
  id: number;
  institution_id: number;
  plan_id: number;
  status: SubscriptionStatus;
  payfast_token: string | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  cancelled_at: string | null;
  plan?: Plan;
}

export interface PayFastCheckout {
  action: string;
  fields: Record<string, string | number>;
  m_payment_id: string;
}

export type WorkflowStepStatus = "pending" | "in_progress" | "completed" | "skipped";
export type WorkflowStatus = "in_progress" | "completed" | "cancelled";

export interface WorkflowStep {
  id: number;
  workflow_id: number;
  stage_name: string;
  stage_order: number;
  is_optional: boolean;
  status: WorkflowStepStatus;
  completed_by: number | null;
  completed_at: string | null;
  started_at: string | null;
  overdue_notified: boolean;
  comments: string | null;
  completer?: User;
}

export interface WorkflowCommentEntry {
  id: number;
  workflow_id: number;
  step_id: number | null;
  user_id: number | null;
  comment: string;
  created_at: string;
  user?: User;
}

export interface WorkflowDocumentEntry {
  id: number;
  workflow_id: number;
  step_id: number | null;
  uploaded_by: number | null;
  original_filename: string;
  stored_path: string;
  mime_type: string | null;
  size: number;
  created_at: string;
  uploader?: User;
}

export interface ContractWorkflow {
  id: number;
  contract_id: number;
  started_by: number | null;
  started_at: string;
  completed_at: string | null;
  current_stage: string | null;
  status: WorkflowStatus;
  steps: WorkflowStep[];
  comments: WorkflowCommentEntry[];
  documents: WorkflowDocumentEntry[];
  starter?: User;
}

export interface WorkflowStats {
  total_active_workflows: number;
  waiting_for_approval: {
    scm: number;
    management: number;
    cfo_hod: number;
  };
  average_completion_percentage: number;
  overdue_workflow_count: number;
  completed_this_month: number;
}

export interface ReportSummary {
  total_contracts: number;
  active_contracts: number;
  pending_renewal: number;
  expired_contracts: number;
  terminated_contracts: number;
  total_value: number;
  active_value: number;
  expiring_30: number;
  expiring_60: number;
  expiring_90: number;
  expiring_180: number;
  expiring_value_90: number;
}

export interface DepartmentReport {
  department: string;
  total_contracts: number;
  active_contracts: number;
  pending_renewal: number;
  expired_contracts: number;
  total_value: string;
  active_value: string;
}

export type ReviewRecommendation = "Auto-Renew" | "Conditional Renewal" | "Re-Advertise";

// ─── Auth ────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string) =>
    api.post<AuthResponse>("/login", { email, password }),

  register: (institution_name: string, name: string, email: string, password: string, password_confirmation: string) =>
    api.post<AuthResponse>("/register", { institution_name, name, email, password, password_confirmation }),

  logout: () => api.post<{ message: string }>("/logout", {}),

  me: () => api.get<SingleResponse<User>>("/user"),
};

// ─── Contracts ───────────────────────────────────────────────────────────────

export const contractsApi = {
  list: (page = 1, filters: { search?: string; status?: string; department?: string; sort?: string; dir?: string } = {}) => {
    const params = new URLSearchParams({ page: String(page) });
    if (filters.search) params.set("search", filters.search);
    if (filters.status) params.set("status", filters.status);
    if (filters.department) params.set("department", filters.department);
    if (filters.sort) params.set("sort", filters.sort);
    if (filters.dir) params.set("dir", filters.dir);
    return api.get<PaginatedResponse<Contract>>(`/contracts?${params}`);
  },

  get: (id: number) =>
    api.get<SingleResponse<Contract> & { health_score: HealthScore }>(`/contracts/${id}`),

  create: (data: Partial<Contract>) =>
    api.post<SingleResponse<Contract>>("/contracts", data),

  update: (id: number, data: Partial<Contract>) =>
    api.patch<SingleResponse<Contract> & { health_score: HealthScore }>(`/contracts/${id}`, data),

  delete: (id: number) =>
    api.delete<void>(`/contracts/${id}`),

  expiringSoon: (days = 30) =>
    api.get<PaginatedResponse<Contract>>(`/contracts/expiring/soon?days=${days}`),

  stats: () =>
    api.get<SingleResponse<ContractStats>>("/contracts/stats"),
};

// ─── Renewal Workflow ────────────────────────────────────────────────────────

export const workflowApi = {
  get: (contractId: number) =>
    api.get<{ data: ContractWorkflow | null; completion_percentage?: number }>(`/contracts/${contractId}/workflow`),

  start: (contractId: number, notes?: string) =>
    api.post<SingleResponse<ContractWorkflow>>(`/contracts/${contractId}/workflow/start`, { notes }),

  updateStep: (
    workflowId: number,
    stepId: number,
    payload: { action: "complete" | "skip" | "reopen"; comments?: string; decision?: "approved" | "new_procurement" }
  ) => api.patch<SingleResponse<WorkflowStep>>(`/workflow/${workflowId}/step/${stepId}`, payload),

  addComment: (workflowId: number, comment: string, stepId?: number) =>
    api.post<SingleResponse<WorkflowCommentEntry>>(`/workflow/${workflowId}/comments`, { comment, step_id: stepId }),

  uploadDocument: (workflowId: number, file: File, stepId?: number) => {
    const formData = new FormData();
    formData.append("file", file);
    if (stepId) formData.append("step_id", String(stepId));
    return api.postForm<SingleResponse<WorkflowDocumentEntry>>(`/workflow/${workflowId}/documents`, formData);
  },

  stats: () =>
    api.get<SingleResponse<WorkflowStats>>("/workflows/stats"),
};

// ─── Departments ─────────────────────────────────────────────────────────────

export const departmentsApi = {
  list: (page = 1) =>
    api.get<PaginatedResponse<Department>>(`/departments?page=${page}`),

  get: (id: number) =>
    api.get<SingleResponse<Department>>(`/departments/${id}`),

  create: (data: Partial<Department>) =>
    api.post<SingleResponse<Department>>("/departments", data),

  update: (id: number, data: Partial<Department>) =>
    api.patch<SingleResponse<Department>>(`/departments/${id}`, data),

  delete: (id: number) =>
    api.delete<void>(`/departments/${id}`),
};

// ─── Suppliers ───────────────────────────────────────────────────────────────

export const suppliersApi = {
  list: (page = 1, options: { search?: string; withLatestReview?: boolean } = {}) => {
    const qs = new URLSearchParams({ page: String(page) });
    if (options.search) qs.set("search", options.search);
    if (options.withLatestReview) qs.set("with_latest_review", "1");
    return api.get<PaginatedResponse<Supplier & { latest_review?: SupplierReview }>>(`/suppliers?${qs}`);
  },

  get: (id: number) =>
    api.get<SingleResponse<Supplier>>(`/suppliers/${id}`),

  create: (data: Partial<Supplier>) =>
    api.post<SingleResponse<Supplier>>("/suppliers", data),

  update: (id: number, data: Partial<Supplier>) =>
    api.patch<SingleResponse<Supplier>>(`/suppliers/${id}`, data),

  delete: (id: number) =>
    api.delete<void>(`/suppliers/${id}`),

  reviews: (id: number, page = 1) =>
    api.get<PaginatedResponse<SupplierReview>>(`/suppliers/${id}/reviews?page=${page}`),

  departments: (id: number) =>
    api.get<SingleResponse<SupplierDepartment[]>>(`/suppliers/${id}/departments`),

  setDepartmentVendorNumber: (supplierId: number, departmentId: number, vendorNumber: string) =>
    api.put<SingleResponse<SupplierDepartment[]>>(`/suppliers/${supplierId}/departments/${departmentId}`, { vendor_number: vendorNumber }),

  removeDepartment: (supplierId: number, departmentId: number) =>
    api.delete<void>(`/suppliers/${supplierId}/departments/${departmentId}`),
};

// ─── Supplier Reviews ────────────────────────────────────────────────────────

export const reviewsApi = {
  list: (page = 1) =>
    api.get<PaginatedResponse<SupplierReview>>(`/reviews?page=${page}`),

  get: (id: number) =>
    api.get<SingleResponse<SupplierReview>>(`/reviews/${id}`),

  create: (data: Partial<SupplierReview>) =>
    api.post<SingleResponse<SupplierReview>>("/reviews", data),

  update: (id: number, data: Partial<SupplierReview>) =>
    api.patch<SingleResponse<SupplierReview>>(`/reviews/${id}`, data),

  delete: (id: number) =>
    api.delete<void>(`/reviews/${id}`),
};

// ─── Audit Logs ──────────────────────────────────────────────────────────────

export const auditLogsApi = {
  forContract: (contractId: number) =>
    api.get<{ data: AuditLog[] }>(`/contracts/${contractId}/audit-logs`),

  create: (contractId: number, data: { action_type: string; action: string; notes?: string; metadata?: Record<string, unknown> }) =>
    api.post<{ data: AuditLog }>(`/contracts/${contractId}/audit-logs`, data),

  list: (params: { contract_id?: number; date_from?: string; date_to?: string; action_type?: string; page?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.contract_id) qs.set("contract_id", String(params.contract_id));
    if (params.date_from) qs.set("date_from", params.date_from);
    if (params.date_to) qs.set("date_to", params.date_to);
    if (params.action_type) qs.set("action_type", params.action_type);
    if (params.page) qs.set("page", String(params.page));
    return api.get<PaginatedResponse<AuditLog>>(`/audit-logs?${qs}`);
  },
};

// ─── Contract Renewals ───────────────────────────────────────────────────────

export const renewalsApi = {
  forContract: (contractId: number) =>
    api.get<{ data: ContractRenewal[] }>(`/contracts/${contractId}/renewals`),

  create: (contractId: number, data: { decision: ContractRenewal["decision"]; justification: string; new_end_date?: string }) =>
    api.post<{ data: ContractRenewal }>(`/contracts/${contractId}/renewals`, data),
};

// ─── Reports ─────────────────────────────────────────────────────────────────

export const reportsApi = {
  summary: () =>
    api.get<{ data: ReportSummary }>("/reports/summary"),

  byDepartment: () =>
    api.get<{ data: DepartmentReport[] }>("/reports/by-department"),

  expiring: (days = 90, department?: string) => {
    const qs = new URLSearchParams({ days: String(days) });
    if (department) qs.set("department", department);
    return api.get<{ data: Contract[] }>(`/reports/expiring?${qs}`);
  },

  allContracts: (filters: { status?: string; department?: string; date_from?: string; date_to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (filters.status) qs.set("status", filters.status);
    if (filters.department) qs.set("department", filters.department);
    if (filters.date_from) qs.set("date_from", filters.date_from);
    if (filters.date_to) qs.set("date_to", filters.date_to);
    return api.get<{ data: Contract[] }>(`/reports/contracts?${qs}`);
  },
};

// ─── SLA Matrix ──────────────────────────────────────────────────────────────

export const slaApi = {
  list: (page = 1) =>
    api.get<PaginatedResponse<SLAMatrix>>(`/sla-matrix?page=${page}`),

  get: (id: number) =>
    api.get<SingleResponse<SLAMatrix>>(`/sla-matrix/${id}`),

  create: (data: Partial<SLAMatrix>) =>
    api.post<SingleResponse<SLAMatrix>>("/sla-matrix", data),

  update: (id: number, data: Partial<SLAMatrix>) =>
    api.patch<SingleResponse<SLAMatrix>>(`/sla-matrix/${id}`, data),

  delete: (id: number) =>
    api.delete<void>(`/sla-matrix/${id}`),
};

// ─── Billing ─────────────────────────────────────────────────────────────────

export const plansApi = {
  list: () => api.get<SingleResponse<Plan[]>>("/plans"),
};

export const billingApi = {
  subscription: () =>
    api.get<{ data: Subscription | null; is_usable?: boolean }>("/billing/subscription"),

  checkout: (planId: number) =>
    api.post<SingleResponse<PayFastCheckout>>("/billing/checkout", { plan_id: planId }),
};
